import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import axios from "../../api/axios";
import { getAttributeValue } from "../../utils/utils";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PROGRAM_ID = "QoHFZ6cd3Nm";

function truncateLabel(label, maxLength = 28) {
  const text = String(label || "");
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

const Table_17 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);
  const [districtName, setDistrictName] = useState("the District Assembly");

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getData();
  }, [selectedYear, district, period]);

  useEffect(() => {
    if (!district) {
      setDistrictName("the District Assembly");
      return;
    }

    axios
      .get(`/organisationUnits/${district}?fields=displayName`)
      .then((response) => {
        setDistrictName(response.data?.displayName || "the District Assembly");
      })
      .catch(() => {
        setDistrictName("the District Assembly");
      });
  }, [district]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_17",
      tableData,
    }),
  }));

  async function getData() {
    if (!district) {
      setTableData([]);
      return;
    }

    try {
      const startDate = `${selectedYear}-01-01`;
      const endDate = `${selectedYear}-12-31`;

      const [entitiesResp, eventsResp] = await Promise.all([
        axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=${PROGRAM_ID}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
        axios.get(
          `/tracker/events?program=${PROGRAM_ID}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
      ]);

      const data = entitiesResp.data.instances || [];
      const reports = eventsResp.data.instances || [];

      const temps = data.map((key, idx) => {
        const currentReport = reports.find((rep) => rep.trackedEntity === key.trackedEntity);
        let allocation = 0;
        let receipt = 0;
        let targetBen = 0;
        let actualFemale = 0;
        let actualMale = 0;

        currentReport?.dataValues?.forEach((rep) => {
          if (rep.dataElement === "HIsJGuRVMqN") {
            allocation += parseFloat(rep.value) || 0;
          } else if (rep.dataElement === "k3tpjhfxiVe") {
            receipt += parseFloat(rep.value) || 0;
          } else if (rep.dataElement === "ijnO4FfRU6u") {
            targetBen += parseFloat(rep.value) || 0;
          } else if (rep.dataElement === "nAvze5yaI4Z") {
            actualFemale += parseFloat(rep.value) || 0;
          } else if (rep.dataElement === "PazSogYpr0w") {
            actualMale += parseFloat(rep.value) || 0;
          }
        });

        return {
          no: idx + 1,
          issues: getAttributeValue("NDPC | CRIT Critical Development and Poverty Issues", key),
          allocation,
          receipt,
          targetBen,
          actualFemale,
          actualMale,
          actualTotal: actualFemale + actualMale,
        };
      });

      setTableData(temps);
    } catch (err) {
      console.error("Error loading Table 17 data:", err);
      setTableData([]);
    }
  }

  const chartData = {
    labels: tableData.map((row) => truncateLabel(row.issues)),
    datasets: [
      {
        label: "Allocation (GH¢)",
        data: tableData.map((row) => row.allocation),
        backgroundColor: "#0d6efd",
        borderColor: "#0a58ca",
        borderWidth: 1,
      },
      {
        label: "Actual Receipt (GH¢)",
        data: tableData.map((row) => row.receipt),
        backgroundColor: "#198754",
        borderColor: "#146c43",
        borderWidth: 1,
      },
      {
        label: "Target Beneficiaries",
        data: tableData.map((row) => row.targetBen),
        backgroundColor: "#ffc107",
        borderColor: "#d39e00",
        borderWidth: 1,
      },
      {
        label: "Actual Beneficiaries",
        data: tableData.map((row) => row.actualTotal),
        backgroundColor: "#dc3545",
        borderColor: "#bd2130",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `Critical Development and Poverty Issues Analysis, ${selectedYear}`,
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const index = items[0]?.dataIndex;
            return tableData[index]?.issues || items[0]?.label;
          },
          label: (context) => {
            const value = context.parsed.y ?? 0;
            if (context.dataset.label?.includes("GH¢")) {
              return `${context.dataset.label}: ${new Intl.NumberFormat("en-GH", {
                style: "currency",
                currency: "GHS",
                minimumFractionDigits: 2,
              }).format(value)}`;
            }
            return `${context.dataset.label}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Amount / Count",
        },
      },
    },
  };

  const formatAmount = (value) => (value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="col-12">
      <h3>Table 17 – Updates on Critical Development and Poverty Issues in the Year {selectedYear}</h3>
      <div className="card">
        <div className="card-header" />
        <div className="card-body">
          <h5>Update on Critical Development and Poverty Issues</h5>

          <h7>
            In the attempt to reduce poverty and promote development, Government has introduced
            a number of interventions in the Country. Among these interventions that are operational
            in the {districtName} are: the National Health Insurance Scheme,
            Ghana School Feeding Programme, Capitation Grant, Livelihood Empowerment Against
            Poverty (LEAP) Programme, National Youth Employment Programme, One District One
            Factory Programme, Planting for Food and Jobs, Free SHS Programme, National
            Entrepreneurship and Innovation Plan (NEIP), Implementation of Infrastructure for
            Poverty Eradication Programme (IPEP) and the Nations Builders Corps (NABCO)
            Programme. Table 2.6 presents a matrix showing the summary of key critical poverty
            development issues, the Allocations, Actual Receipt and the Number of beneficiaries.
            Details of the various interventions have been given below.
          </h7>

          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table17-${year}`}
            hideTableDis={hideTableDis}
          />

          <div className="table-responsive">
            <table className="table table-bordered" style={{ marginTop: "20px" }}>
              <thead style={{ backgroundColor: "#d4edda", fontWeight: "bold", border: "1px solid #000" }}>
                <tr>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Critical Development and Poverty Issues</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Allocation GH₵</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Actual Receipt GH₵</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Number of Beneficiaries Target</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Number of Beneficiaries Actual</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Number of Beneficiaries Actual (Female)</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Number of Beneficiaries Actual (Male)</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #000" }}>{row.issues}</td>
                      <td style={{ border: "1px solid #000" }}>{formatAmount(row.allocation)}</td>
                      <td style={{ border: "1px solid #000" }}>{formatAmount(row.receipt)}</td>
                      <td style={{ border: "1px solid #000" }}>{row.targetBen.toLocaleString()}</td>
                      <td style={{ border: "1px solid #000" }}>{row.actualTotal.toLocaleString()}</td>
                      <td style={{ border: "1px solid #000" }}>{row.actualFemale.toLocaleString()}</td>
                      <td style={{ border: "1px solid #000" }}>{row.actualMale.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ border: "1px solid #000", textAlign: "center" }}>
                      No data available for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h4 className="mt-4">Figure 17: Critical Development and Poverty Issues Analysis</h4>
          <div style={{ height: "420px" }}>
            {tableData.length > 0 ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <p className="text-center">No chart data available for {selectedYear}</p>
            )}
          </div>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table17-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_17;
